import { type ReactNode, useMemo } from 'react'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly } from '@sevenrooms/timepiece'
import { Flex } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { RequestOrPriorityAlertButton } from '../../../components/RequestOrPriorityAlertButton'
import { TimeSlotButton } from '../../../components/TimeSlotButton'
import { reservationWidgetMessages } from '../../../reservationWidgetMessages'
import type { ExperienceWithAvailability } from '../../../hooks/useAvailability'

interface ExperienceNoAvailabilityProps {
  experience: ExperienceWithAvailability
  partySize?: number
  startDate?: string
  isPrivateEvent: boolean
  onRequestBtnClick: (experience: ExperienceWithAvailability, requestDate: string, requestPartySize: number) => void
  onPriorityAlertBtnClick: (experience: ExperienceWithAvailability, requestDate: string, requestPartySize: number) => void
  onClearTimeFilter?: () => void
}

export function ExperienceNoAvailability({
  experience,
  partySize,
  startDate,
  isPrivateEvent,
  onRequestBtnClick,
  onPriorityAlertBtnClick,
  onClearTimeFilter,
}: ExperienceNoAvailabilityProps) {
  const { formatMessage } = useLocales()
  const { totalCapacityMax, totalCapacityMin, reservationTimesInHalo, reservationTimes } = experience
  const showClearTimeButton = !!startDate && onClearTimeFilter && reservationTimesInHalo?.length === 0 && !!reservationTimes?.length

  const noAvailabilityText = useMemo(() => {
    if (showClearTimeButton) {
      return formatMessage(reservationWidgetMessages.resWidgetNoAvailability)
    }
    if (!reservationTimesInHalo) {
      return formatMessage(reservationWidgetMessages.resWidgetPrivateEventsSpecifySearchCriteriaText)
    }
    if (!reservationTimesInHalo?.length && partySize) {
      if (totalCapacityMin !== undefined && partySize < totalCapacityMin) {
        return formatMessage(reservationWidgetMessages.resWidgetPrivateEventsSelectHigherPartySizeText, {
          value: totalCapacityMin,
        })
      }
      if (totalCapacityMax !== undefined && partySize > totalCapacityMax) {
        return formatMessage(reservationWidgetMessages.resWidgetPrivateEventsSelectLowerPartySizeText, {
          value: totalCapacityMax,
        })
      }
    }
    return formatMessage(reservationWidgetMessages.resWidgetPrivateEventsNoAvailabilityAtSelectedDayText)
  }, [formatMessage, partySize, reservationTimesInHalo, showClearTimeButton, totalCapacityMax, totalCapacityMin])

  const actionButton = useMemo(() => {
    if (showClearTimeButton) {
      return (
        <TimeSlotButton
          data-test="sr-room-card-no-availability-clear-time-button"
          variant="tertiary"
          width="initial"
          onClick={onClearTimeFilter}
        >
          {formatMessage(reservationWidgetMessages.resWidgetShowAllTimes, {
            date: DateOnly.from(startDate).formatSMonthNDaySWeek(),
          })}
        </TimeSlotButton>
      )
    }
    if (!startDate || !partySize) {
      return null
    }
    return (
      <RequestOrPriorityAlertButton
        partySize={partySize}
        buttonVariant="tertiary"
        isPrivateEvent={isPrivateEvent}
        onPriorityAlertButtonClick={() => onPriorityAlertBtnClick(experience, startDate, partySize)}
        onRequestButtonClick={() => onRequestBtnClick(experience, startDate, partySize)}
      />
    )
  }, [
    experience,
    formatMessage,
    isPrivateEvent,
    onClearTimeFilter,
    onPriorityAlertBtnClick,
    onRequestBtnClick,
    partySize,
    showClearTimeButton,
    startDate,
  ])
  return <NoAvailabilityContainer text={noAvailabilityText} actionButton={actionButton} />
}

function NoAvailabilityContainer({ text, actionButton }: { text: string; actionButton?: ReactNode }) {
  return (
    <Flex display="block">
      {/* body1bold but with 22px line height */}
      <Text whiteSpace="break-spaces" lineHeight="l" fontSize="m" fontWeight={500} data-test="sr-room-card-no-availability-text">
        {text}&nbsp;
      </Text>
      {actionButton}
    </Flex>
  )
}
